'use client';

/**
 * useFundRecovery Hook
 *
 * Handles withdraw and emergency withdraw operations for recovering funds
 * from abandoned protocols.
 */

import { useState, useCallback } from 'react';
import { useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import type { Address } from 'viem';

interface WithdrawConfig {
  standardWithdrawAbi: any;
  emergencyWithdrawAbi?: any;
  recommendation: 'standard' | 'emergency';
}

interface UseFundRecoveryReturn {
  // Standard withdraw
  withdraw: (
    contractAddress: Address,
    poolId: number,
    amount: bigint,
    abi: any
  ) => void;
  // Emergency withdraw (forfeits rewards)
  emergencyWithdraw: (
    contractAddress: Address,
    poolId: number,
    abi: any
  ) => void;
  // State
  isWithdrawing: boolean;
  txHash: string | null;
  isSuccess: boolean;
  isError: boolean;
  error: Error | null;
  // Reset
  reset: () => void;
}

export function useFundRecovery(): UseFundRecoveryReturn {
  const [lastOperation, setLastOperation] = useState<'withdraw' | 'emergency' | null>(null);

  const {
    writeContract,
    data: txHash,
    isPending: isWritePending,
    error: writeError,
    reset: resetWrite,
  } = useWriteContract();

  const {
    isLoading: isWaitingForTx,
    isSuccess,
    isError: isTxError,
    error: txError,
  } = useWaitForTransactionReceipt({
    hash: txHash,
  });

  const withdraw = useCallback(
    (contractAddress: Address, poolId: number, amount: bigint, abi: any) => {
      setLastOperation('withdraw');
      writeContract({
        address: contractAddress,
        abi: [abi],
        functionName: abi.name,
        args: [BigInt(poolId), amount],
      });
    },
    [writeContract]
  );

  const emergencyWithdraw = useCallback(
    (contractAddress: Address, poolId: number, abi: any) => {
      setLastOperation('emergency');
      writeContract({
        address: contractAddress,
        abi: [abi],
        functionName: abi.name,
        args: [BigInt(poolId)],
      });
    },
    [writeContract]
  );

  const reset = useCallback(() => {
    resetWrite();
    setLastOperation(null);
  }, [resetWrite]);

  return {
    withdraw,
    emergencyWithdraw,
    isWithdrawing: isWritePending || isWaitingForTx,
    txHash: txHash ?? null,
    isSuccess,
    isError: isTxError || !!writeError,
    error: writeError || txError || null,
    reset,
  };
}
